import { useEffect, useState } from 'react';
import { useEntitlement } from '../../app/session.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetFlowResult, type ListPalettesResult, type FlowCostWarningsResult } from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  FlowCanvas,
  Icon,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import type { FlowGraph, Palette } from '@whinerp/contracts/flow';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { flowCanvasLabels } from './flow-labels.js';

/**
 * SC-21 Automation builder.
 *
 * The same canvas the journey builder uses, given the automation palette. The
 * activate control is blocked by a list of what is wrong, each drawn on the
 * node it is about - because a flow that is switched on while incomplete stops
 * halfway and reports success, which is the least visible failure this product
 * can have.
 */

function AutomationBuilderScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const params = useParams();
  const flowId = params['id'] ?? '';

  const [graph, setGraph] = useState<FlowGraph | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const flow = useQuery<GetFlowResult, ApiError>({
    queryKey: ['flow', flowId],
    queryFn: () => api.getFlow({ path: { flowId } }),
  });

  const palettes = useQuery<ListPalettesResult, ApiError>({
    queryKey: ['palettes'],
    queryFn: () => api.listPalettes({}),
  });

  useEffect(() => {
    if (flow.data && graph === null) setGraph(flow.data.graph as FlowGraph);
  }, [flow.data, graph]);

  const save = useMutation({
    mutationFn: (status: 'draft' | 'active') =>
      api.saveFlow({
        path: { flowId },
        body: { graph: (graph ?? { nodes: [], edges: [] }) as never, status },
      }),
    onSuccess: (result) => {
      toast.show({
        tone: 'success',
        title: t('state.saved'),
        body: result.status === 'active' ? t('flow.active') : t('flow.draft'),
      });
      void flow.refetch();
    },
    onError: (error: ApiError) =>
      // The server names every problem at once rather than one per attempt.
      toast.show({ tone: 'error', title: t('flow.problems'), body: error.message }),
  });

  const testRun = useMutation({
    mutationFn: () => api.testFlow({ path: { flowId }, body: { record: { amount: '5000.00' } } }),
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  if (flow.isError) return <BuilderError error={flow.error} retry={() => void flow.refetch()} />;
  if (flow.isPending || !graph) return <BuilderLoading />;

  const palette = (palettes.data?.palettes.find((entry) => entry.key === flow.data.palette) ??
    null) as Palette | null;
  if (!palette) return <BuilderLoading />;

  const problems = flow.data.problems;

  return (
    <div data-component="AutomationBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{flow.data.name}</PageHeading>
        <span
          data-flow-status={flow.data.status}
          className="inline-flex h-chip items-center rounded-chip bg-sunken px-2 text-meta text-ink-600"
        >
          {flow.data.status === 'active' ? t('flow.active') : t('flow.draft')}
        </span>

        <span className="ms-auto flex items-center gap-2">
          <Button intent="secondary" data-save-draft onClick={() => save.mutate('draft')}>
            {t('action.save')}
          </Button>
          <Button
            intent="primary"
            data-activate
            // Blocked, with the list beside it. A disabled control that does
            // not say why is a dead control with a better disguise.
            disabled={problems.length > 0 || save.isPending}
            title={problems.length > 0 ? t('flow.problems') : undefined}
            onClick={() => save.mutate('active')}
          >
            {t('flow.activate')}
          </Button>
        </span>
      </header>

      {problems.length > 0 ? (
        <Panel data-region="problems">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('flow.problems')}</SectionLabel>
            <ul className="flex flex-col">
              {problems.map((problem) => (
                <li
                  key={`${problem.nodeId ?? 'flow'}-${problem.message}`}
                  className="flex items-center gap-1 text-dense text-danger-text"
                >
                  <Icon name="Warning" size="inline" decorative />
                  {problem.message}
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}

      <CostWarnings flowId={flow.data.flowId} />
      <TriggerSettings graph={graph} onChange={setGraph} />
      <Panel data-region="canvas">
        <FlowCanvas
          graph={graph}
          palette={palette}
          labels={flowCanvasLabels(t)}
          problems={problems}
          onChange={setGraph}
          selectedNodeId={selected}
          onSelectNode={setSelected}
          {...(testRun.data ? { executed: testRun.data.steps } : {})}
        />
      </Panel>

      <Panel data-region="test">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('flow.test')}</SectionLabel>
          <Button
            intent="secondary"
            data-test-run
            disabled={testRun.isPending}
            onClick={() => testRun.mutate()}
          >
            {t('flow.testRun')}
          </Button>
          {testRun.data ? (
            <Num data-test-outcome className="text-dense text-ink">
              {testRun.data.outcome} ·{' '}
              {t('flow.run.steps', {
                values: { count: formatCount(testRun.data.steps.length, format) },
              })}
            </Num>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

function BuilderEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="FlowArrow"
      title={t('flow.empty.title')}
      body={t('flow.empty.body')}
      primary={{ label: t('empty.action.addStep'), icon: 'Plus', href: '/work' }}
    />
  );
}

function BuilderLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <Skeleton className="h-block-2xl w-full" />
    </div>
  );
}

function BuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This automation could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-21',
  route: '/automations/:id',
  surface: 'page',
  title: 'Automation builder',
  // F-089. SC-21 is the trigger-condition-action builder; F-068 is the
  // marketing journey canvas, which is SC-20.
  features: ['F-089'],
  entitlement: 'F-068',
  reachedBy: [{ kind: 'setup', section: 'automation' }],
  components: ['FlowCanvas'],
  elements: ['AutomationBuilder'],
  sampleRecord: { objectKey: 'flow', param: 'id' },
  states: {
    empty: BuilderEmpty,
    loading: BuilderLoading,
    error: BuilderError,
  },
  component: AutomationBuilderScreen,
});

export default screen;

/** What is likely to be expensive about this automation, while it is being built (F-096). */
function CostWarnings({ flowId }: { flowId: string }): JSX.Element | null {
  const t = useT();
  const enabled = useEntitlement('F-096');
  const warnings = useQuery<FlowCostWarningsResult, ApiError>({
    queryKey: ['flow-cost', flowId],
    queryFn: () => api.flowCostWarnings({ path: { flowId } }),
    enabled: enabled.enabled,
  });
  if (!warnings.data || warnings.data.warnings.length === 0) return null;
  return (
    <div data-cost-warnings role="status" className="flex flex-col gap-1 rounded-card bg-warning-tint px-3 py-2">
      <span className="text-dense text-ink">{t('governor.designWarning')}</span>
      {warnings.data.warnings.map((warning, index) => (
        <span key={`${warning.kind}-${String(index)}`} data-cost-warning={warning.kind} className="text-meta text-warning-text">
          {warning.message}
        </span>
      ))}
    </div>
  );
}

/** Objects a record trigger can watch (G-220). */
const TRIGGER_OBJECTS = ['contact', 'account', 'lead', 'deal', 'quote', 'support_case', 'campaign'] as const;
const FIELD_INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

/**
 * What starts a record-event automation (G-220): which object, and for a
 * change, optionally one field and the value it changes to.
 */
function TriggerSettings({ graph, onChange }: { graph: FlowGraph; onChange: (next: FlowGraph) => void }): JSX.Element | null {
  const t = useT();
  const trigger = graph.nodes.find((node) => node.kind === 'trigger');
  if (!trigger || !trigger.type.startsWith('record-')) return null;
  const config = trigger.config ?? {};
  const text = (key: string): string => (typeof config[key] === 'string' ? (config[key] as string) : '');
  const set = (key: string, value: string) =>
    onChange({
      ...graph,
      nodes: graph.nodes.map((node) => (node.id === trigger.id ? { ...node, config: { ...config, [key]: value } } : node)),
    });
  return (
    <Panel data-region="trigger-settings">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('recordTrigger.title')}</SectionLabel>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('recordTrigger.object')}
            <select className={FIELD_INPUT} data-trigger-object value={text('object')} onChange={(event) => set('object', event.target.value)}>
              <option value="">{t('recordTrigger.choose')}</option>
              {TRIGGER_OBJECTS.map((key) => <option key={key} value={key}>{t(`recordTrigger.objectName.${key}`)}</option>)}
            </select>
          </label>
          {trigger.type === 'record-updated' ? (
            <>
              <label className="flex flex-col gap-1 text-meta text-ink-meta">
                {t('recordTrigger.field')}
                <input className={FIELD_INPUT} data-trigger-field value={text('field')} placeholder={t('recordTrigger.anyField')} onChange={(event) => set('field', event.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-meta text-ink-meta">
                {t('recordTrigger.to')}
                <input className={FIELD_INPUT} data-trigger-to value={text('to')} placeholder={t('recordTrigger.anyValue')} disabled={!text('field').trim()} onChange={(event) => set('to', event.target.value)} />
              </label>
            </>
          ) : null}
        </div>
        <p className="text-meta text-ink-meta">{t('recordTrigger.note')}</p>
      </div>
    </Panel>
  );
}
